import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getEffectiveTenantScope } from '../services/patientService';
import { hasAnyScorecard, isBonusEnabled } from '../services/bonusService';

const cache = new Map();

/**
 * Whether bonus features apply to the signed-in user in the effective tenant:
 * `enabled` (tenant gate), `hasScorecard` (enrolled at some point → My Bonus link).
 */
export default function useBonusAccess() {
  const { profile, permissions } = useAuth();
  const tenantId = getEffectiveTenantScope(profile).tenantId || '';
  const uid = profile?.uid || '';
  const relevant = Boolean(tenantId && uid && (permissions.canSeeMyBonus || permissions.canManageBonusPrograms));
  const key = `${tenantId}|${uid}`;
  const [state, setState] = useState(() => cache.get(key) || { loading: relevant, enabled: false, hasScorecard: false });

  useEffect(() => {
    if (!relevant) {
      setState({ loading: false, enabled: false, hasScorecard: false });
      return undefined;
    }
    if (cache.has(key)) {
      setState(cache.get(key));
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const enabled = await isBonusEnabled(tenantId);
        const hasScorecard = enabled && permissions.canSeeMyBonus ? await hasAnyScorecard(tenantId, uid) : false;
        const next = { loading: false, enabled, hasScorecard, tenantId };
        cache.set(key, next);
        if (!cancelled) setState(next);
      } catch {
        if (!cancelled) setState({ loading: false, enabled: false, hasScorecard: false });
      }
    })();
    return () => { cancelled = true; };
  }, [key, relevant, tenantId, uid, permissions.canSeeMyBonus]);

  return { ...state, tenantId };
}
